import {Component, effect, inject, Input, OnDestroy, OnInit, Signal} from '@angular/core';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {IUser} from '../../../user-access/models';
import {Store} from '@ngrx/store';
import {selectUser} from '../../../user-access/user-store/user.selectors';
import {
  selectUserAccessFeatureAutoOrderingSettingsForSelectedStore
} from '../../../user-access/user-access-store/user-access.selectors';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {
  _VALID_AUTO_ORDERING_TOKEN_KEYS,
  getFieldErrorString,
  PHONE_PATTERN_REGEX,
  VAR_ALL_REGEX
} from '../../../../shared/utils/form/form.utils';
import {
  selectSelectedSetting,
  selectSelectedStoreOrderSettings
} from '../../store/setting.selectors';
import {SettingsReferenceEnum} from '../../models';
import {settingsSetSelected, settingUserStoreOrderConfiguration} from '../../store/setting.actions';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {DEFAULT_APPLICATION_TEXT, _DEFAULT_APPLICATION_TOOLTIPS} from '../../../core/models';
import {selectSelectedStore} from '../../../user-access/user-stores-store/user-store.selectors';
import {IStore} from '../../../../shared/shared-models/store/store';
import {IAutoOrderOrderingSettings} from '../../models';
import {
  IUserAccessFeatureAutoOrderingSettings
} from 'src/app/features/user-access/models/user-access-features/access/user-access-feature-auto-ordering';
import {ICrudOptions} from '../../../user-access/models/user-access-features/access';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {ISharedModalBasic} from 'src/app/shared/shared-models/modals/shared-modal-basic';
import {IModalDismissResult} from 'src/app/shared/shared-services/overlay/modal-controller.service';
import {Ascii} from '../../../../shared/shared-models/global/ascii.enum';
import {IHasUnsavedChanges, UnsavedChangesService} from '../../services';

@Component({
  selector: 'app-auto-order-order-settings',
  templateUrl: './feature-auto-order-order-settings.component.html',
  styleUrl: './feature-auto-order-order-settings.component.scss',
  standalone: false
})
export class FeatureAutoOrderOrderSettingsComponent implements OnInit, OnDestroy, IHasUnsavedChanges {

  //#region INJECTORS
  private formBuilder = inject(FormBuilder);
  private _store = inject(Store);
  private modalUtils = inject(SharedModalUtils);
  private unsavedChangesService = inject(UnsavedChangesService);
  //#endregion

  //#region INPUTS
  @Input() hideActionBar: boolean = false;
  //#endregion

  //#region PROPERTIES
  formOrderingSettings: FormGroup;
  #indexCCRecipients: string[] = [];
  #indexCCRecipientsCount: number = undefined;

  protected readonly icons = IconsEnum;
  protected readonly requiredFieldsNote = 'Required fields must be set.';
  protected readonly iconColours = IconColourEnum;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;
  protected readonly placeholders = DEFAULT_APPLICATION_TEXT;

  private readonly _settingChanged: Signal<SettingsReferenceEnum> = this._store.selectSignal(selectSelectedSetting);
  private readonly _orderSettings: Signal<IAutoOrderOrderingSettings> = this._store.selectSignal(selectSelectedStoreOrderSettings);
  private readonly _user: Signal<IUser> = this._store.selectSignal(selectUser);
  private readonly _selectedStore: Signal<IStore> = this._store.selectSignal(selectSelectedStore);
  private readonly _uaOrderingSettings: Signal<IUserAccessFeatureAutoOrderingSettings> = this._store.selectSignal(selectUserAccessFeatureAutoOrderingSettingsForSelectedStore);
  private readonly _validTokenKeys: {[token: string]: string} = {..._VALID_AUTO_ORDERING_TOKEN_KEYS};

  //#endregion

  protected readonly utilText = {
    actions: {
      insertVariable: {
        tooltip: 'Inserts a variable into the field.',
        text: 'Insert Variable',
      },
    },
    ccRecipient: {
      tooltip: 'The email address to CC orders to. This is useful for a staff member to keep track of all outgoing orders.',
    },
    contactName: {
      tooltip: 'The contact name used in the email body if added as a variable in the template below.',
    },
    contactNumber: {
      tooltip: 'The phone number used in the email body if added as a variable in the template below.',
    },
    deliveryClause: {
      tooltip: 'This adds a delivery clause to the bottom of the purchase order PDF.',
    },
    emailBody: {
      tooltip: 'Customise the body within your email.',
    },
    emailSubject: {
      tooltip: 'This will be the subject line for the email.',
    },
    orderNumber: {
      tooltip: 'This is used as the order number when generating the purchase order on IQ, it acts as a reference for the supplier.',
    },
  };

  //#region INITIALIZATION
  constructor() {
    effect(() => {
      const userAccess = this._uaOrderingSettings();
      const order = this._orderSettings();
      this.initializeForms(userAccess, order);
    });
    effect(() => {
      const ref = this._settingChanged();
      this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
    });
    effect(() => {
      const store = this._selectedStore();
      if (store?.storeId) {
        this.formOrderingSettings?.reset();
        this._store.dispatch(settingUserStoreOrderConfiguration.getOrderConfiguration({}));
      }
    });

    this.unsavedChangesService.registerComponent(this);
  }

  //#endregion

  //#region ACCESSOR - ORDERING SETTINGS

  get hasOrderingSettingsBeenEdited(): boolean {
    return this.formOrderingSettings?.dirty ?? false;
  }

  get hasOrderingSettingsErrors(): {
    errors: ValidationErrors; error: string; field: string;
  }[] {
    const errors: {errors: ValidationErrors; error: string; field: string;}[] = [];
    const orderNumErrors = this.formOrderingSettings.controls['orderNumber']?.errors;
    const contactNameErrors = this.formOrderingSettings.controls['contactName']?.errors;
    const contactNumErrors = this.formOrderingSettings.controls['contactNumber']?.errors;
    const bodyErrors = this.formOrderingSettings.controls['body']?.errors;
    const subjectErrors = this.formOrderingSettings.controls['subject']?.errors;
    if (orderNumErrors) {
      errors.push({
        errors: orderNumErrors,
        error: getFieldErrorString(orderNumErrors, 'orderNumber'),
        field: 'orderNumber'
      });
    }
    if (contactNameErrors) {
      errors.push({
        errors: contactNameErrors,
        error: getFieldErrorString(contactNameErrors, 'contactName'),
        field: 'contactName'
      });
    }
    if (contactNumErrors) {
      errors.push({
        errors: contactNumErrors,
        error: getFieldErrorString(contactNumErrors, 'contactNumber'),
        field: 'contactNumber'
      });
    }
    this.#indexCCRecipients.forEach((recipient) => {
      const temp = this.formOrderingSettings.controls[`ccRecipients-${recipient}`]?.errors;
      if (!!temp) {
        errors.push({
          errors: temp,
          error: getFieldErrorString(temp, 'ccRecipients'),
          field: 'ccRecipients'
        });
      }
    });
    if (subjectErrors) {
      errors.push({errors: subjectErrors, error: getFieldErrorString(subjectErrors, 'subject'), field: 'subject'});
    }
    if (bodyErrors) {
      errors.push({errors: bodyErrors, error: getFieldErrorString(bodyErrors, 'body'), field: 'body'});
    }
    return errors;
  }

  get isOrderingSettingsSubmitDisabled(): boolean {
    return (this.canOrderingSettingsBeEdited === false) || (this.canOrderingSettingsBeEdited === true && this.hasOrderingSettingsBeenEdited === false) || (this.formOrderingSettings?.invalid ?? true);
  }

  get canOrderingSettingsBeEdited(): boolean {
    const access = this.orderSettingsUserAccess?.orderSettings;
    return access?.canEdit ?? false;
  }

  get canOrderingSettingsBeSubmitted(): boolean {
    const access = this.orderSettingsUserAccess?.orderSettings;
    return access?.canSubmit ?? false;
  }

  get canOrderingSettingsBeViewed(): boolean {
    const access = this.orderSettingsUserAccess?.orderSettings;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSOR - USER SETTINGS + USER ACCESS + STORE
  get selectedStore(): IStore {
    return this._selectedStore();
  }

  get userSettings(): IUser {
    return this._user();
  }

  get orderSettingsUserAccess(): IUserAccessFeatureAutoOrderingSettings {
    return this._uaOrderingSettings();
  }

  //#endregion

  //#region ACCESSOR - GLOBALS
  get haveAnyEditsBeenMade(): boolean {
    return (this.formOrderingSettings?.dirty ?? false);
  }

  get indexCcRecipients(): string[] {
    return this.#indexCCRecipients;
  }

  get orderSetting(): IAutoOrderOrderingSettings {
    return this._orderSettings();
  }

  get textAreaPlaceholder(): string {
    return `Please see attached order as per subject line.\nPlease call or reply to this email if you have any queries.\n${this.orderSetting.contactName ?? 'Rep Name'} will be available on ${this.orderSetting?.contactNumber ?? 'Number Unavailable'} for further assistance.`;
  }

  //#endregion


  //#region LIFECYCLE HOOKS
  ngOnInit(): void {
    this.formOrderingSettings = this.formBuilder.group({});
  }

  ngOnDestroy(): void {
    this.unsavedChangesService.unregisterComponent();
  }

  //#endregion

  //#region FORM - CLICK HANDLERS
  onRevertForm(form: string): void {
    switch (form) {
      case 'all':
        this.revertOrderDetails();
        break;
      case 'orderingSettings':
        this.revertOrderDetails();
        break;
    }
  }

  onSubmitForm(form: string): void {
    let data: Partial<IAutoOrderOrderingSettings> = {...this.orderSetting};
    switch (form) {
      case 'all':
      case 'orderingSettings':
        if (!this.canOrderingSettingsBeSubmitted) break;
        data = {...data, ...this.generateOrderDetailsPatch(data)};
        this.formOrderingSettings.reset();
        break;
      default:
        break;
    }
    this._store.dispatch(settingUserStoreOrderConfiguration.setOrderConfiguration({orderSettings: data}));
  }

  //#endregion

  //#region FORM - BUILD FORMS FROM ACCESS

  private initializeForms(access: IUserAccessFeatureAutoOrderingSettings, order: IAutoOrderOrderingSettings): void {
    this.buildOrderingSettingsForm(access?.orderSettings, order);
  }

  private buildOrderingSettingsForm(access: ICrudOptions, order: IAutoOrderOrderingSettings): void {
    if (access?.canView && this.formOrderingSettings.pristine) {
      const {body, ccRecipients, contactName, contactNumber, orderNumber, pdfClause, subject} = order;
      const tempForm = this.formBuilder.group({});
      const filtered: string[] = Array.isArray(ccRecipients)
        ? ccRecipients.filter((r) => !!r)
        : ccRecipients
          ? [ccRecipients]
          : [];
      const recipients: string[] = filtered.length > 0 ? filtered : [''];

      this.addFormControl(tempForm, 'orderNumber', orderNumber ?? undefined, false, [Validators.required]);
      this.addFormControl(tempForm, 'contactName', contactName ?? undefined, false, [Validators.required]);
      this.addFormControl(tempForm, 'contactNumber', contactNumber ?? undefined, false, [Validators.required, Validators.pattern(PHONE_PATTERN_REGEX)]);
      this.#indexCCRecipientsCount = 0;
      this.#indexCCRecipients = [];
      recipients.forEach((recipient, $index) => {
        this.addFormControl((tempForm), `ccRecipients-${$index}`, recipient ?? undefined, false, [Validators.email]);
        this.#indexCCRecipients.push(String(this.#indexCCRecipientsCount));
        this.#indexCCRecipientsCount++;
      });
      this.addFormControl(tempForm, 'subject', subject?.trim() || this.placeholders.features.orderSettings.subjectPlaceholder, false,
        [
          Validators.required,
          Validators.pattern(VAR_ALL_REGEX)
        ]);
      this.addFormControl(tempForm, 'body', body?.trim() || this.textAreaPlaceholder, false,
        [
          Validators.required,
          Validators.pattern(VAR_ALL_REGEX)
        ]);
      this.addFormControl(tempForm, 'pdfClause', pdfClause?.text ?? undefined, false, []);
      tempForm.markAsPristine();
      this.formOrderingSettings = tempForm;
    }
  }

  private addFormControl<T = unknown>(form: FormGroup, controlName: string, value: T, disabled: boolean, validators: ValidatorFn[] = []): void {
    form.addControl(controlName, this.formBuilder.control({value, disabled}, validators));
  }

  //#endregion

  //#region FORM - ON CLICK

  revertToPlaceholder(field: 'subject' | 'body'): void {
    const value = field === 'subject'
      ? this.placeholders.features.orderSettings.subjectPlaceholder
      : this.textAreaPlaceholder;
    this.formOrderingSettings.controls[field].setValue(value);
    this.formOrderingSettings.controls[field].markAsDirty();
  }

  onAddRecipientClick(): void {
    this.addFormControl(this.formOrderingSettings, `ccRecipients-${this.#indexCCRecipientsCount}`, undefined, false, [Validators.email]);
    this.#indexCCRecipients.push(String(this.#indexCCRecipientsCount));
    this.#indexCCRecipientsCount++;
  }

  onRemoveRecipientClick(index: string, arrayIndex: number): void {
    if (arrayIndex === 0) {
      const control = this.formOrderingSettings.get('ccRecipients-0');

      control?.setValue('');
      control?.markAsDirty();
      control?.markAsTouched();
      return;
    }

    this.#indexCCRecipients = this.indexCcRecipients.filter((cc) => index !== cc);
    this.formOrderingSettings.removeControl(`ccRecipients-${index}`);
  }

  //#endregion

  //#region FORM - REVERT FORM EDITS
  private revertOrderDetails(): void {
    this.formOrderingSettings.reset({
      body: this.orderSetting?.body?.trim() || this.textAreaPlaceholder,
      ccRecipients: this.orderSetting?.ccRecipients ?? undefined,
      contactName: this.orderSetting?.contactName ?? undefined,
      contactNumber: this.orderSetting?.contactNumber ?? undefined,
      orderNumber: this.orderSetting?.orderNumber ?? undefined,
      pdfClause: this.orderSetting?.pdfClause?.text ?? undefined,
      subject: this.orderSetting?.subject?.trim() || this.placeholders.features.orderSettings.subjectPlaceholder
    });
  }

  //#endregion

  //#region FORM - GENERATE PATCH DATA

  private generateOrderDetailsPatch(data: Partial<IAutoOrderOrderingSettings>): Partial<IAutoOrderOrderingSettings> {
    const orderForm = this.formOrderingSettings.getRawValue();

    const currentOrderNumber = this.orderSetting?.orderNumber?.trim();
    const currentContactName = this.orderSetting?.contactName?.trim();
    const currentContactNumber = this.orderSetting?.contactNumber?.trim();
    const currentSubject = this.orderSetting?.subject?.trim();
    const currentBody = this.orderSetting?.body?.trim();
    const currentPDFClause = this.orderSetting?.pdfClause?.text?.trim();
    const currentRecipients: string[] = Array.isArray(this.orderSetting?.ccRecipients)
      ? this.orderSetting.ccRecipients
      : this.orderSetting?.ccRecipients
        ? [this.orderSetting.ccRecipients]
        : [];

    const nextOrderNumber = orderForm?.orderNumber?.trim();
    const nextContactName = orderForm?.contactName?.trim();
    const nextContactNumber = orderForm?.contactNumber?.trim();
    const nextSubject = orderForm?.subject?.trim();
    const nextBody = orderForm?.body?.trim();
    const nextPDFClause = orderForm?.pdfClause?.trim();

    const ccRecipients: string[] = [];
    this.#indexCCRecipients.forEach((index) => {
      const temp = orderForm[`ccRecipients-${index}`]?.trim();
      if (temp) {
        ccRecipients.push(temp);
      }
    });

    const recipientsChanged =
      JSON.stringify(currentRecipients) !== JSON.stringify(ccRecipients);

    const orderPatch: Partial<IAutoOrderOrderingSettings> = {
      ...(currentOrderNumber !== nextOrderNumber && nextOrderNumber && {orderNumber: nextOrderNumber}),
      ...(currentContactName !== nextContactName && nextContactName && {contactName: nextContactName}),
      ...(currentContactNumber !== nextContactNumber && nextContactNumber && {contactNumber: nextContactNumber}),
      ...(currentSubject !== nextSubject && nextSubject && {subject: nextSubject}),
      ...(currentBody !== nextBody && nextBody && {body: nextBody}),
      ...(currentPDFClause !== nextPDFClause && nextPDFClause && {pdfClause: nextPDFClause})
    };

    if (!(Object.keys(orderPatch).length > 0 || recipientsChanged)) {
      return data;
    }

    data = {
      ...data,
      ...orderPatch,
      ...(recipientsChanged && {ccRecipients})
    };

    return data;
  }

  //#endregion

  //#region SELECT - OPTIONS
  onSelectTokens(selected: {[key: string]: ISelectOption}, property: string): void {
    const selection = Object.keys(selected)
      .filter((storeId: string) => selected[storeId].selected);
    let temp: string = this.formOrderingSettings.getRawValue()[property] ?? '';
    Object.entries(selection).forEach(([_, value]) => {
      const tempValue = value.split('{');
      if (tempValue?.length > 1) {
        temp += ` {${tempValue[1]}`;
      }
    });
    this.formOrderingSettings.controls[property].setValue(temp.trim());
  }

  tokenOptions(): {[key: string]: ISelectOption} {
    const options: {[key: string]: ISelectOption} = {};
    Object.entries(this._validTokenKeys).forEach(([token, value]) => {
      options[`${value} ${Ascii.ArrowRight} ${token}`] = {
        name: `${value} ${Ascii.ArrowRight} ${token}`,
        selected: false,
        disabled: false
      };
    });
    return options;
  }

  //#endregion

  //#region MODAL - DISCARD UNSAVED CHANGES
  async onShowModalUnsavedChanges(): Promise<boolean> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Discard Changes & Continue',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Unsaved Changes',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'If you leave this page, your changes will be lost.',
      contextDescription: ['Are you sure you want to continue?'],
      cssClass: ['shared-basic-modal-css-small']
    };

    const result: IModalDismissResult = await this.modalUtils.createModal(modalProps);
    return result.role === 'modal-accept';
  }

  //#endregion

}
